import {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useNullableContext } from 'shared/hooks/useNullableContext';
import { UpdateParams, useLocationParams } from 'shared/routing/LocationParams';
import { UpgradePlanDialog } from 'carrier/loads/core/UpgradePlanDialog';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';

import {
  ReportsOverviewParamsDTO,
  reportsOverviewParamsSchema,
} from './ReportsDTO';
import { buildLast30DaysDates } from './usePeriodFilters';
import { useShowSubscriptionCancelledDialog } from 'shared/errors/data/useShowSubscriptionCancelledDialog';

interface ReportsOverviewContext {
  params: ReportsOverviewParamsDTO;
  setParams: UpdateParams<ReportsOverviewParamsDTO>;
  isReportingLocked: boolean;
  requestUpgrade: () => void;
}

const Context = createContext<null | ReportsOverviewContext>(null);

export function useReportsOverviewContext() {
  return useNullableContext('ReportsOverview', Context);
}

interface Props {
  children?: ReactNode;
}

export function ReportsOverviewContextProvider({ children }: Props) {
  const [params, setParams] = useLocationParams<ReportsOverviewParamsDTO>({
    zodSchema: reportsOverviewParamsSchema,
    persistName: 'ReportsOverview',
  });
  const { isOpenSubscriptionCancelledDialog } =
    useShowSubscriptionCancelledDialog();

  const { data: settings, isLoading: isSettingsLoading } = useCarrierSettings();
  const [showPlanUpdate, setShowPlanUpdate] = useState(false);

  const isReportingLocked =
    isSettingsLoading || isOpenSubscriptionCancelledDialog
      ? false
      : !settings?.can_open_reporting;

  const requestUpgrade = useCallback(() => {
    setShowPlanUpdate(true);
  }, []);

  const lockedParams = useMemo<ReportsOverviewParamsDTO>(() => {
    const [start_date, end_date] = buildLast30DaysDates();
    return { ...params, start_date, end_date };
  }, [params]);

  const ctx = useMemo<ReportsOverviewContext>(
    () => ({
      params: isReportingLocked ? lockedParams : params,
      setParams,
      isReportingLocked,
      requestUpgrade,
    }),
    [isReportingLocked, lockedParams, params, setParams, requestUpgrade],
  );

  return (
    <Context.Provider value={ctx}>
      {children}

      <UpgradePlanDialog
        open={showPlanUpdate && !isOpenSubscriptionCancelledDialog}
        onClose={() => {
          setShowPlanUpdate(false);
        }}
        type="reports"
        source="Reports"
      />
    </Context.Provider>
  );
}
